import { useMemo } from 'react';
import { keyBy, compact, some } from 'lodash';
import { TELCO_CHANNELS } from '../constants';
import { EXCLUDED_SEARCH_CHANNELS } from 'data/audience-channels';
import { hasPurchaseAffinitiesSegment } from 'data/audience-segment-builder-helper';

const useAudienceConfig = ({ propsAudienceChannel, propsSegment, context, abiPermissions }) => {
  const audienceChannel = useMemo(
    () => propsAudienceChannel || context.current.audience_app.current_channel.value,
    [propsAudienceChannel, context]
  );

  const segment = useMemo(
    () => propsSegment || context.current.audience_app[audienceChannel].audience_segment,
    [propsSegment, context, audienceChannel]
  );

  const isFirstParty = useMemo(() => some(segment, { type: '1st party' }), [segment]);
  // 1st party and purchase affinities both move a Web audience to the bidstream partition, which
  // has no search data (see EXCLUDED_SEARCH_CHANNELS) — so neither may offer Searches. PHOB-1889.
  const isBidstreamBacked = useMemo(
    () => isFirstParty || hasPurchaseAffinitiesSegment(segment),
    [isFirstParty, segment]
  );

  const includeSearches = useMemo(
    () =>
      abiPermissions.hasPermission(['search']) &&
      !isBidstreamBacked &&
      !TELCO_CHANNELS.includes(audienceChannel) &&
      !EXCLUDED_SEARCH_CHANNELS.includes(audienceChannel),
    [abiPermissions, isBidstreamBacked, audienceChannel]
  );

  const interestTypes = useMemo(
    () =>
      compact([
        { value: 'websites', label: 'Websites', plural: 'websites', singular: 'website' },
        { value: 'phrases', label: 'Phrases', plural: 'phrases', singular: 'phrase' },
        includeSearches && { value: 'searches', label: 'Searches', plural: 'searches', singular: 'search term' },
      ]),
    [includeSearches]
  );

  const interestTypesMap = useMemo(() => keyBy(interestTypes, 'value'), [interestTypes]);

  const targetRatio = context.current.audience_segment_target_ratio;
  const targetRatioObj = targetRatio ? { 'wanted-intenders-ratio': targetRatio } : undefined;

  return {
    audienceChannel,
    segment,
    isFirstParty,
    includeSearches,
    interestTypes,
    interestTypesMap,
    targetRatioObj,
  };
};

export default useAudienceConfig;
